import { BLUE, SUCCESS_TEXT, WARNING_TEXT } from '../teacherStyles';

export type LessonWorkspaceTab = 'overview' | 'student' | 'speaking-review';

export const lessonWorkspaceTabs: LessonWorkspaceTab[] = ['overview', 'student', 'speaking-review'];

export const lessonProfiles = {
    'ordering-food-at-a-restaurant': {
        title: 'Ordering Food at a Restaurant',
        description: 'Polite requests, measure words, and a realistic restaurant conversation.',
        duration: '50-minute class plan',
        exerciseCount: 5,
        available: true,
        status: 'Published',
    },
    'restaurant-language-review': {
        title: 'Restaurant Language Review',
        exerciseCount: 3,
        available: true,
        status: 'Published',
    },
    'paying-the-bill': {
        title: 'Paying the Bill',
        description: 'Prices, totals, and payment language.',
        duration: '45-minute class plan',
        exerciseCount: 4,
        available: false,
        status: 'Draft',
    },
    'daily-routines-and-time': {
        title: 'Daily Routines and Time',
        description: 'Daily activities, telling time, and sequencing a typical school day.',
        duration: '50-minute class plan',
        exerciseCount: 5,
        available: true,
        status: 'Published',
    },
};

export type LessonProfile = (typeof lessonProfiles)[keyof typeof lessonProfiles];

export const lessonStudents = [
    { initials: 'AL', name: 'Ava Liu', completion: 100, accuracy: 94, aiScore: 91, teacherScore: 94, review: 'Reviewed', lastActive: 'Today, 9:14 AM' },
    { initials: 'JM', name: 'Jonah Miller', completion: 100, accuracy: 86, aiScore: 78, teacherScore: null, review: '1 awaiting review', lastActive: 'Yesterday' },
    { initials: 'SK', name: 'Sofia Kim', completion: 80, accuracy: 82, aiScore: 84, teacherScore: null, review: 'Up to date', lastActive: 'Yesterday' },
    { initials: 'NP', name: 'Noah Patel', completion: 60, accuracy: 71, aiScore: 68, teacherScore: null, review: '1 awaiting review', lastActive: '2 days ago' },
    { initials: 'EC', name: 'Emma Chen', completion: 40, accuracy: 65, aiScore: 62, teacherScore: null, review: '1 awaiting review', lastActive: '3 days ago' },
    { initials: 'LR', name: 'Lucas Rivera', completion: 0, accuracy: 0, aiScore: null, teacherScore: null, review: 'Not started', lastActive: '6 days ago' },
];

export const lessonExerciseSummaries = [
    { name: 'Vocabulary review', type: 'Vocabulary', completed: '18 of 18', accuracy: 91, speaking: '—' },
    { name: 'Polite request builder', type: 'Grammar', completed: '17 of 18', accuracy: 84, speaking: '—' },
    { name: 'Measure word practice', type: 'Guided practice', completed: '16 of 18', accuracy: 68, speaking: '—' },
    { name: 'Restaurant conversation', type: 'Speaking', completed: '14 of 18', accuracy: 79, speaking: '76 attempts' },
    { name: 'Spoken exit ticket', type: 'Assessment', completed: '14 of 18', accuracy: 81, speaking: '14 attempts' },
];

export const lessonExerciseInsights = [
    { label: 'Strongest exercise', result: 'Vocabulary review · 91%', note: 'All 18 students completed it.', color: SUCCESS_TEXT },
    { label: 'Needs attention', result: 'Measure word practice · 68%', note: 'Review classifiers before the next lesson.', color: WARNING_TEXT },
    { label: 'Speaking participation', result: '90 attempts', note: '3 recordings are awaiting teacher feedback.', color: BLUE },
];

export const initialSpeakingAttempts = [
    {
        id: 'attempt-1042',
        initials: 'JM',
        student: 'Jonah Miller',
        exercise: 'Restaurant conversation',
        submitted: 'Today, 10:42 AM',
        prompt: 'Ask the server for one plate of dumplings and a cup of tea.',
        expected: '请给我一盘饺子和一杯茶。',
        transcript: '请给我一盘饺子和一杯茶。',
        aiScore: 78,
        aiFeedback: 'Clear overall. The tone on 盘 was uncertain, and 茶 was slightly shortened.',
        pronunciation: [
            { character: '请', pinyinExpected: 'qǐng', pinyinSpoken: 'qǐng', initial: 'good', final: 'good', tone: 'good', alignment: 'speech' },
            { character: '给', pinyinExpected: 'gěi', pinyinSpoken: 'gěi', initial: 'good', final: 'good', tone: 'good', alignment: 'speech' },
            { character: '我', pinyinExpected: 'wǒ', pinyinSpoken: 'wǒ', initial: 'good', final: 'good', tone: 'good', alignment: 'speech' },
            { character: '一', pinyinExpected: 'yì', pinyinSpoken: 'yì', initial: 'good', final: 'good', tone: 'good', alignment: 'speech' },
            { character: '盘', pinyinExpected: 'pán', pinyinSpoken: 'pán', initial: 'good', final: 'good', tone: 'ok', alignment: 'speech' },
            { character: '饺', pinyinExpected: 'jiǎo', pinyinSpoken: 'jiǎo', initial: 'good', final: 'good', tone: 'good', alignment: 'speech' },
            { character: '子', pinyinExpected: 'zi', pinyinSpoken: 'zi', initial: 'good', final: 'good', tone: 'good', alignment: 'speech' },
            { character: '和', pinyinExpected: 'hé', pinyinSpoken: 'hé', initial: 'good', final: 'good', tone: 'good', alignment: 'speech' },
            { character: '一', pinyinExpected: 'yì', pinyinSpoken: 'yì', initial: 'good', final: 'good', tone: 'good', alignment: 'speech' },
            { character: '杯', pinyinExpected: 'bēi', pinyinSpoken: 'bēi', initial: 'good', final: 'good', tone: 'good', alignment: 'speech' },
            { character: '茶', pinyinExpected: 'chá', pinyinSpoken: 'cha', initial: 'good', final: 'ok', tone: 'bad', alignment: 'speech' },
        ],
        status: 'Awaiting review',
    },
    {
        id: 'attempt-1038',
        initials: 'NP',
        student: 'Noah Patel',
        exercise: 'Spoken exit ticket',
        submitted: 'Yesterday, 3:18 PM',
        prompt: 'Politely ask whether the restaurant has noodles.',
        expected: '请问，你们有面条吗？',
        transcript: '请问，你们有面条吗？',
        aiScore: 68,
        aiFeedback: 'The sentence was understandable. 请问 and 面条 need clearer tone contours.',
        pronunciation: [
            { character: '请', pinyinExpected: 'qǐng', pinyinSpoken: 'qing', initial: 'good', final: 'good', tone: 'ok', alignment: 'speech' },
            { character: '问', pinyinExpected: 'wèn', pinyinSpoken: 'wen', initial: 'good', final: 'good', tone: 'bad', alignment: 'speech' },
            { character: '你', pinyinExpected: 'nǐ', pinyinSpoken: 'nǐ', initial: 'good', final: 'good', tone: 'good', alignment: 'speech' },
            { character: '们', pinyinExpected: 'men', pinyinSpoken: 'men', initial: 'good', final: 'good', tone: 'good', alignment: 'speech' },
            { character: '有', pinyinExpected: 'yǒu', pinyinSpoken: 'yǒu', initial: 'good', final: 'good', tone: 'good', alignment: 'speech' },
            { character: '面', pinyinExpected: 'miàn', pinyinSpoken: 'mian', initial: 'good', final: 'ok', tone: 'bad', alignment: 'speech' },
            { character: '条', pinyinExpected: 'tiáo', pinyinSpoken: 'tiao', initial: 'good', final: 'good', tone: 'ok', alignment: 'speech' },
            { character: '吗', pinyinExpected: 'ma', pinyinSpoken: 'ma', initial: 'good', final: 'good', tone: 'good', alignment: 'speech' },
        ],
        status: 'Awaiting review',
    },
    {
        id: 'attempt-1029',
        initials: 'EC',
        student: 'Emma Chen',
        exercise: 'Restaurant conversation',
        submitted: 'Monday, 1:05 PM',
        prompt: 'Ask for the bill and thank the server.',
        expected: '请给我账单，谢谢。',
        transcript: '请给我账单，谢谢。',
        aiScore: 62,
        aiFeedback: 'Good sentence structure. 账单 was difficult to distinguish in the recording.',
        pronunciation: [
            { character: '请', pinyinExpected: 'qǐng', pinyinSpoken: 'qǐng', initial: 'good', final: 'good', tone: 'good', alignment: 'speech' },
            { character: '给', pinyinExpected: 'gěi', pinyinSpoken: 'gěi', initial: 'good', final: 'good', tone: 'good', alignment: 'speech' },
            { character: '我', pinyinExpected: 'wǒ', pinyinSpoken: 'wǒ', initial: 'good', final: 'good', tone: 'good', alignment: 'speech' },
            { character: '账', pinyinExpected: 'zhàng', pinyinSpoken: '—', initial: 'bad', final: 'bad', tone: 'bad', alignment: 'silence' },
            { character: '单', pinyinExpected: 'dān', pinyinSpoken: 'dan', initial: 'ok', final: 'bad', tone: 'bad', alignment: 'speech' },
            { character: '-', pinyinExpected: '-', pinyinSpoken: 'le', initial: 'bad', final: 'bad', tone: 'bad', alignment: 'extra' },
            { character: '谢', pinyinExpected: 'xiè', pinyinSpoken: 'xie', initial: 'good', final: 'good', tone: 'ok', alignment: 'speech' },
            { character: '谢', pinyinExpected: 'xie', pinyinSpoken: 'xie', initial: 'good', final: 'good', tone: 'good', alignment: 'speech' },
        ],
        status: 'Awaiting review',
    },
];
